/**
 * Shared tenant routing utility.
 *
 * Centralises the "where should this user go after login / account switch?"
 * logic so it lives in exactly one place. Consumed by:
 *   - login/page.tsx  (resolvePostLoginRoute) — context: 'login'
 *   - AccountSwitcherDropdown.tsx  (handleAccountSwitch) — context: 'switch'
 *
 * Behaviour differs by context:
 *   - 'login':  Fresh login. Multi-tenant users always see the picker (clear active tenant).
 *   - 'switch': Account switch. Multi-tenant users restore their saved tenant if available.
 */

import store, { AppDispatch } from '@/app/store';
import { fetchUserTenants } from '@/app/store/thunks/tenantThunks';
import { setActiveTenant } from '@/app/store/slices/tenantSlice';
import { workspaceHref } from '@/app/features/workspace/utils/workspaceRoute';

const PREFERENCE_RESTORE_TIMEOUT_MS = 2000;
const PREFERENCE_RESTORE_POLL_MS = 100;

/**
 * Polls Redux state until activeTenant is set or timeout is reached.
 * TenantLoader restores the saved tenant from DB preferences asynchronously —
 * this waits for that to complete rather than using a fixed delay.
 */
function waitForTenantRestore(): Promise<boolean> {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      const { activeTenant } = store.getState().tenant;
      if (activeTenant) {
        resolve(true);
        return;
      }
      if (Date.now() - start > PREFERENCE_RESTORE_TIMEOUT_MS) {
        resolve(false);
        return;
      }
      setTimeout(check, PREFERENCE_RESTORE_POLL_MS);
    };
    check();
  });
}

export interface ResolveRouteResult {
  destination: string;
}

/**
 * Resolves the correct page for a user based on their tenants and force-home config.
 *
 * @param context - 'login' for fresh login (shows picker for multi-tenant),
 *                  'switch' for account switch (restores saved tenant if available)
 */
export async function resolveUserRoute(
  dispatch: AppDispatch,
  userId: number,
  context: 'login' | 'switch' = 'login'
): Promise<ResolveRouteResult> {
  try {
    const result = await dispatch(fetchUserTenants(userId));

    if (fetchUserTenants.fulfilled.match(result)) {
      const { tenants, forceWorkspaceHome } = result.payload;

      // Single tenant → auto-select and go to that tenant's workspace
      if (tenants.length === 1) {
        dispatch(setActiveTenant({
          tenantId: String(tenants[0].id),
          tenantCode: tenants[0].tenantCode,
          tenantName: tenants[0].tenantName,
          configurationJson: tenants[0].configurationJson ?? null,
        }));
        return { destination: workspaceHref({ tenantCode: tenants[0].tenantCode }) };
      }

      // Multi-tenant
      if (tenants.length > 1) {
        if (context === 'switch') {
          // Account switch: try to restore saved tenant from DB preferences
          const restored = await waitForTenantRestore();
          if (restored) {
            // waitForTenantRestore resolved true → activeTenant is now set.
            const restoredTenant = store.getState().tenant.activeTenant;
            return {
              destination: restoredTenant ? workspaceHref(restoredTenant) : '/main',
            };
          }
        } else {
          // Fresh login: always clear active tenant so picker shows
          dispatch(setActiveTenant(null));
        }

        // Show picker on /main
        sessionStorage.removeItem('sm_tenant_picker_dismissed');

        if (forceWorkspaceHome) {
          // force_home → /main with non-dismissable picker
          return { destination: '/main' };
        }

        return { destination: '/main' };
      }
    }
  } catch {
    // Fall through to default
  }

  return { destination: '/main' };
}
